// biome-ignore-all lint/suspicious/noExplicitAny: not changing all these now
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ActivityLogDto,
  FeedbackFilterSchemaDto,
  IdeaSummaryDto,
  LifecycleFilterType,
  ProductAreaDto,
} from '@tempo-io/fim3-dto';
import {
  type CreateFeedbackAttachmentDto,
  CreateFeedbackCommentSchema,
  type FeedbackCommentDto,
  type FeedbackDto,
  type FeedbackFilterType,
  type FeedbackStatusSummaryDto,
  type FeedbackSummaryItemDto,
  type GetAllParamsDto,
  type GetFeedbackCommentResponseDto,
  type GetFeedbackResponseDto,
  type GetFeedbacksResponseDto,
  LifecycleFilter,
  type UpdateFeedbackCommentDto,
  type UpdateFeedbackDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import type { z } from 'zod';
// import { Fim3ApiClient } from '@tempo-io/fim3-api-client';

import { useErrorHandlingMutation } from './useErrorHandlingMutation';
import { getUserDataForResults } from './useFim3Api';
import { useS3Api } from './useS3Api';

export const CreateFeedbackCommentUISchema = CreateFeedbackCommentSchema.omit({
  commenterId: true,
});
export type CreateFeedbackCommentDto = z.infer<typeof CreateFeedbackCommentUISchema>;

export const useFeedbackApi = (httpClient: AxiosInstance) => {
  const baseUrl = '/feedback';
  const s3Api = useS3Api(httpClient);
  const defaultLimit = 25;

  const getFeedbacks = async (
    params?: GetAllParamsDto & {
      stateFilter?: FeedbackFilterType;
      filters?: FeedbackFilterSchemaDto;
    },
  ): Promise<GetFeedbacksResponseDto> => {
    const { data } = await httpClient.post<GetFeedbacksResponseDto>(`${baseUrl}/query`, params);
    return data;
  };

  const getFeedback = async (
    id: number,
    lifecycleFilter?: LifecycleFilterType,
    stateFilter?: FeedbackFilterType,
    search?: string,
    productAreaIds?: number[],
    formIds?: number[],
  ): Promise<GetFeedbackResponseDto> => {
    const { data } = await httpClient
      .get(`${baseUrl}/${id}`, {
        params: {
          lifecycleFilter,
          stateFilter,
          search,
          productAreaIds,
          formIds,
        },
      })
      .catch((error) => {
        if (error.response?.status === 404) {
          return { data: null };
        }
        throw error;
      });
    return data;
  };

  const processFeedback = async ({ id }: { id: number }): Promise<{ data: FeedbackDto }> => {
    const { data } = await httpClient.post(`${baseUrl}/${id}/process`);
    return data;
  };

  const unprocessFeedback = async ({ id }: { id: number }): Promise<{ data: FeedbackDto }> => {
    const { data } = await httpClient.put(`${baseUrl}/${id}`, {
      processedAt: null,
    });
    return data;
  };

  const linkIdeaToFeedback = async ({
    feedbackId,
    ideaId,
  }: {
    feedbackId: number;
    ideaId: number;
  }): Promise<{
    feedbackId: number;
    ideaId: number;
  }> => {
    const { data } = await httpClient.post(`/ideas/${ideaId}/feedback/${feedbackId}/link`);
    return data;
  };

  const unlinkIdeaFromFeedback = async ({
    feedbackId,
    ideaId,
  }: {
    feedbackId: number;
    ideaId: number;
  }): Promise<
    {
      feedbackId: number;
      ideaId: number;
    }[]
  > => {
    const { data } = await httpClient.delete(`/ideas/${ideaId}/feedback/${feedbackId}/unlink`);
    return data;
  };

  const getFeedbackProducts = async (feedbackId: number): Promise<ProductAreaDto[]> => {
    const { data } = await httpClient.get(`${baseUrl}/${feedbackId}/products`);
    return data;
  };

  const updateFeedbackProducts = async (dto: {
    id: number;
    productIds: number[];
  }): Promise<{ id: number; productIds: number[] }> => {
    const { data } = await httpClient.put(`${baseUrl}/${dto.id}/products`, dto.productIds);
    return data;
  };

  const getFeedbacksStatusSummary = async (): Promise<FeedbackStatusSummaryDto> => {
    const { data } = await httpClient.get(`${baseUrl}/status-summary`);
    return data;
  };

  const getFeedbackIdeas = async (feedbackId: number): Promise<IdeaSummaryDto[]> => {
    const { data } = await httpClient.get(`${baseUrl}/${feedbackId}/ideas`);
    return data;
  };

  const archiveFeedback = async (feedbackId: number): Promise<{ data: FeedbackDto }> => {
    const { data } = await httpClient.put(`${baseUrl}/${feedbackId}/archive`);
    return data;
  };

  const restoreFeedback = async (feedbackId: number): Promise<{ data: FeedbackDto }> => {
    const { data } = await httpClient.put(`${baseUrl}/${feedbackId}/restore`);
    return data;
  };

  const deleteFeedback = async (feedbackId: number): Promise<{ data: FeedbackDto }> => {
    const { data } = await httpClient.delete(`${baseUrl}/${feedbackId}`);
    return data;
  };

  const createFeedbackComment = async (
    comment: CreateFeedbackCommentDto,
  ): Promise<GetFeedbackCommentResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}/${comment.feedbackId}/comment`, comment);
    return data;
  };
  const updateFeedbackComment = async (
    comment: UpdateFeedbackCommentDto & { id: number; feedbackId: number },
  ): Promise<GetFeedbackCommentResponseDto> => {
    const { id, feedbackId, ...rest } = comment;
    const { data } = await httpClient.put(`${baseUrl}/${feedbackId}/comment/${id}`, {
      content: rest.content,
      customerVisible: rest.customerVisible,
    });
    return data;
  };

  const archiveFeedbackComment = async (params: {
    id: number;
    feedbackId: number;
  }): Promise<{ data: FeedbackCommentDto }> => {
    const { data } = await httpClient.put(
      `${baseUrl}/${params.feedbackId}/comment/${params.id}/archive`,
    );
    return data;
  };

  const restoreFeedbackComment = async (params: {
    id: number;
    feedbackId: number;
  }): Promise<{ data: FeedbackCommentDto }> => {
    const { data } = await httpClient.put(
      `${baseUrl}/${params.feedbackId}/comment/${params.id}/restore`,
    );
    return data;
  };

  const deleteFeedbackComment = async (params: {
    id: number;
    feedbackId: number;
  }): Promise<{ data: FeedbackCommentDto }> => {
    const { data } = await httpClient.delete(
      `${baseUrl}/${params.feedbackId}/comment/${params.id}`,
    );
    return data;
  };

  const createFeedbackAttachment = async ({
    file,
    attachment,
  }: {
    file: File;
    attachment: CreateFeedbackAttachmentDto;
  }): Promise<GetFeedbackCommentResponseDto> => {
    const key = await new Promise((resolve) => {
      s3Api.uploadAttachment.mutate(file, { onSuccess: (key) => resolve(key) });
    });

    const { data } = await httpClient.post(`${baseUrl}/${attachment.feedbackId}/attachments`, {
      ...attachment,
      attachmentKey: key,
    });

    return data;
  };

  const removeFeedbackAttachment = async (params: { feedbackId: number; id: number }) => {
    const { data } = await httpClient.delete(
      `${baseUrl}/${params.feedbackId}/attachments/${params.id}`,
    );
    return data;
  };

  const queryClient = useQueryClient();
  const useGetAllQuery = (
    params?: GetAllParamsDto & {
      stateFilter?: FeedbackFilterType;
      filters?: FeedbackFilterSchemaDto;
    },
    enabled?: boolean,
  ) => {
    const limit = params?.limit ?? defaultLimit;
    return useQuery({
      gcTime: 0,
      queryKey: [baseUrl, 'GetAllQuery', params?.lifecycleFilter, params],
      queryFn: () => {
        const newParams = { ...params, limit };
        return getFeedbacks(newParams);
      },
      enabled: enabled ?? true,
    });
  };

  const useGetAll = (
    params?: GetAllParamsDto & {
      stateFilter?: FeedbackFilterType;
      filters?: FeedbackFilterSchemaDto;
    },
  ) => {
    const limit = params?.limit ?? defaultLimit;
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: [baseUrl, 'GetAll', params?.lifecycleFilter, params],
      queryFn: ({ pageParam }) => {
        const newParams = { ...params, limit, offset: pageParam * limit };
        return getFeedbacks(newParams);
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined;
      },
    });
  };

  const useGetOne = (id: number, disable?: boolean) =>
    useQuery({
      queryKey: [baseUrl, id],
      queryFn: () => getFeedback(id),
      enabled: !disable,
    });

  const useLinkIdeaToFeedback = () =>
    useErrorHandlingMutation({
      mutationFn: linkIdeaToFeedback,
      onSuccess: (data) => {
        return queryClient.invalidateQueries({
          queryKey: [baseUrl, data.feedbackId, 'ideas'],
        });
      },
    });

  const useUnlinkIdeaFromFeedback = () =>
    useErrorHandlingMutation({
      mutationFn: unlinkIdeaFromFeedback,
      onSuccess: (data) => {
        if (!data[0]?.feedbackId) return;

        return queryClient.invalidateQueries({ queryKey: [baseUrl, data[0].feedbackId, 'ideas'] });
      },
    });

  const useProcessFeedback = (id: number, skipInvalidation = false) =>
    useErrorHandlingMutation({
      mutationFn: processFeedback,
      onSuccess: () => {
        if (!skipInvalidation) {
          queryClient.invalidateQueries({ queryKey: [baseUrl] });
        }
        feedbackSetCachedData({
          id,
          processedAt: new Date(),
        } as FeedbackSummaryItemDto);
      },
    });

  const useUnprocessFeedback = (skipInvalidation = false) =>
    useErrorHandlingMutation({
      mutationFn: unprocessFeedback,
      onSuccess: (data) => {
        if (!skipInvalidation) {
          queryClient.invalidateQueries({ queryKey: [baseUrl] });
        }
        feedbackSetCachedData(data.data as FeedbackSummaryItemDto);
      },
    });

  const useGetFeedbackStatusSummary = () => {
    return useQuery({
      queryKey: [baseUrl, 'status-summary'],
      queryFn: () => getFeedbacksStatusSummary(),
    });
  };

  const useGetFeedbackIdeas = (feedbackId: number) => {
    return useQuery({
      queryKey: [baseUrl, feedbackId, 'ideas'],
      queryFn: () => getFeedbackIdeas(feedbackId),
    });
  };

  const useGetFeedbackProducts = (feedbackId: number) => {
    return useQuery({
      queryKey: [baseUrl, feedbackId, 'products'],
      queryFn: () => getFeedbackProducts(feedbackId),
    });
  };

  const useUpdateFeedbackProducts = () => {
    return useErrorHandlingMutation({
      mutationFn: updateFeedbackProducts,
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: [baseUrl, data.id, 'products'],
        });
        queryClient.invalidateQueries({ queryKey: [baseUrl] });
        // queryClient.resetQueries({
        //   queryKey: [baseUrl, 'GetAll', LifecycleFilter.Active],
        // });
      },
    });
  };

  const useArchiveFeedback = () =>
    useErrorHandlingMutation({
      mutationFn: archiveFeedback,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl] }),
          queryClient.resetQueries({
            queryKey: [baseUrl, 'GetAll', LifecycleFilter.Archived],
          }),
        ]),
    });

  const useRestoreFeedback = () =>
    useErrorHandlingMutation({
      mutationFn: restoreFeedback,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl] }),
          queryClient.resetQueries({
            queryKey: [baseUrl, 'GetAll', LifecycleFilter.Active],
          }),
        ]),
    });

  const useDeleteFeedback = () =>
    useErrorHandlingMutation({
      mutationFn: deleteFeedback,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [baseUrl] });
      },
    });

  const getFeedbackAttachments = async (id: number) => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/attachments`);
    return data;
  };

  const useGetFeedbackAttachments = (id: number) =>
    useQuery({
      queryKey: [baseUrl, id, 'attachments'],
      queryFn: () => getFeedbackAttachments(id),
    });

  const getFeedbackComments = async (id: number) => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/comments`);

    const userIds = data.data.map((el: FeedbackCommentDto) => el.commenterId);

    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data.data,
      (item: any) => item.commenterId,
    );

    return result;
  };

  const useGetFeedbackComments = ({ id }: { id: number }) =>
    useQuery({
      queryKey: [baseUrl, id, 'comments'],
      queryFn: () => getFeedbackComments(id),
    });

  const createComment = useErrorHandlingMutation({
    mutationFn: createFeedbackComment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const updateComment = useErrorHandlingMutation({
    mutationFn: updateFeedbackComment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const useArchiveFeedbackComment = () =>
    useErrorHandlingMutation({
      mutationFn: archiveFeedbackComment,
      onSuccess: (data) =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [baseUrl, data.data.feedbackId, 'comments'],
          }),
        ]),
    });

  const useRestoreFeedbackComment = () =>
    useErrorHandlingMutation({
      mutationFn: restoreFeedbackComment,
      onSuccess: (data) =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [baseUrl, data.data.feedbackId, 'comments'],
          }),
        ]),
    });

  const useDeleteFeedbackComment = () =>
    useErrorHandlingMutation({
      mutationFn: deleteFeedbackComment,
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: [baseUrl, data.data.feedbackId, 'comments'],
        });
      },
    });

  const getFeedbackActivityLogs = async (id: number, logType = '') => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/activity-logs`, {
      params: { logType },
    });

    const userIds = data.map((el: ActivityLogDto) => el.userId);

    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data,
      (item: any) => item.userId,
    );

    return result;
  };

  const useGetFeedbackActivityLogs = (id: number, logType = '') =>
    useQuery({
      queryKey: [baseUrl, id, 'activity-logs', logType],
      queryFn: () => getFeedbackActivityLogs(id, logType),
    });

  const updateFeedback = async (update: UpdateFeedbackDto & { id: number }) => {
    const { id, ...rest } = update;
    const { data } = await httpClient.put(`${baseUrl}/${id}`, rest);

    return data;
  };

  // Update Feedback data in the react-query cache.
  const feedbackSetCachedData = (feedback: FeedbackSummaryItemDto) => {
    queryClient.setQueryData(
      [baseUrl, feedback.id],
      (oldData: { data: FeedbackSummaryItemDto & { dummy: Date } }) => {
        if (oldData?.data?.id === feedback.id) {
          // Using 'dummy' to force a re-render when toggling processedAt between a Date and null!
          oldData.data = { ...oldData.data, ...feedback, dummy: new Date() };
        }
        // Using structuredClone because returned items are supposed to be replaced, not modified!
        return structuredClone(oldData);
      },
    );

    const queryKeys = queryClient
      .getQueryCache()
      .getAll()
      .filter((q) => {
        return q.queryKey[0] === baseUrl && q.queryKey[1] === 'GetAllQuery';
      });

    // update paged query caches
    queryKeys.forEach((key) => {
      queryClient.setQueryData(
        key.queryKey,
        (oldData: { data: (FeedbackSummaryItemDto & { dummy: Date })[] } | undefined) => {
          if (oldData) {
            oldData.data = oldData.data || [];
            for (let k = 0; k < oldData.data?.length; k++) {
              if (oldData.data?.[k]?.id === feedback.id) {
                oldData.data[k] = {
                  ...oldData.data?.[k],
                  ...feedback,
                  dummy: new Date(),
                };
                break;
              }
            }
          }
          return structuredClone(oldData);
        },
      );
    });
  };

  const useUpdateFeedback = useErrorHandlingMutation({
    mutationFn: updateFeedback,
    onSuccess: (data) => {
      feedbackSetCachedData(data.data);
    },
  });

  const createAttachment = useErrorHandlingMutation({
    mutationFn: createFeedbackAttachment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  const removeAttachment = useErrorHandlingMutation({
    mutationFn: removeFeedbackAttachment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
  });

  return {
    useGetOne,
    useGetAll,
    useGetAllQuery,
    useProcessFeedback,
    useUnprocessFeedback,
    useGetFeedbackStatusSummary,
    useGetFeedbackIdeas,
    useLinkIdeaToFeedback,
    useUnlinkIdeaFromFeedback,
    useUpdateFeedbackProducts,
    useGetFeedbackProducts,
    useArchiveFeedback,
    useRestoreFeedback,
    useDeleteFeedback,
    useGetFeedbackAttachments,
    useGetFeedbackComments,
    createComment,
    updateComment,
    useArchiveFeedbackComment,
    useRestoreFeedbackComment,
    useDeleteFeedbackComment,
    useGetFeedbackActivityLogs,
    feedbackSetCachedData,
    useUpdateFeedback,
    createAttachment,
    removeAttachment,
  };
};
